import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FaTicketAlt, FaTrophy, FaHandshake, FaChartLine, 
  FaFire, FaClock, FaUsers, FaArrowRight,
  FaWallet, FaStar, FaTrendingUp, FaLock,
  FaShieldAlt, FaGem, FaRocket, FaCheck,
  FaPlay, FaCrown, FaDollarSign, FaMedal,
  FaFootballBall, FaBasketballBall, FaGlobe,
  FaMobile, FaHeadset, FaGift, FaHeart,
  FaChevronLeft, FaChevronRight, FaQuoteLeft,
  FaRobot, FaExchangeAlt, FaLightbulb, FaShare,
  FaCopy, FaUserPlus, FaGift as FaGiftIcon,
  FaPercentage
} from 'react-icons/fa';
import './Home.css';


const Home = () => {
  const { isAuthenticated } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Slider Data
  const slides = [
    {
      id: 1,
      icon: <FaUsers />,
      title: 'Fans to Fans Marketplace',
      description: 'Join thousands of passionate fans and predict on your favorite club games.',
      highlight: 'Predict & Win with Your Club',
      color: '#f7971e',
      image: '🏆'
    },
    {
      id: 2,
      icon: <FaRobot />,
      title: 'Automated Payouts',
      description: 'Smart contracts handle payouts automatically when your prediction wins.',
      highlight: 'Instant Automated Payments',
      color: '#2ecc71',
      image: '⚡'
    },
    {
      id: 3,
      icon: <FaHandshake />,
      title: 'Compete with Fellow Fans',
      description: 'Challenge other fans of your club or rival clubs. Prove you know your team best.',
      highlight: 'Head-to-Head Fan Battles',
      color: '#3498db',
      image: '🤝'
    },
    {
      id: 4,
      icon: <FaFootballBall />,
      title: 'Predict on Any Match',
      description: 'From Premier League to La Liga, predict on any match worldwide.',
      highlight: 'Global Match Coverage',
      color: '#9b59b6',
      image: '🌍'
    },
    {
      id: 5,
      icon: <FaExchangeAlt />,
      title: 'Win Alongside Your Club',
      description: 'When your favorite club wins, you win too! Share the victory with fellow fans.',
      highlight: 'Win Together, Celebrate Together',
      color: '#e74c3c',
      image: '🎉'
    }
  ];

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Features data
  const features = [
    {
      icon: <FaTicketAlt />,
      title: 'Share Bet Codes',
      description: 'Post winning bet codes and help fellow fans win.',
      color: '#f7971e'
    },
    {
      icon: <FaTrophy />,
      title: 'Prediction Competitions',
      description: 'Join competitions, predict match outcomes, and win prizes.',
      color: '#3498db'
    },
    {
      icon: <FaHandshake />,
      title: 'Fans vs Fans H2H',
      description: 'Challenge rival fans head-to-head. Prove your club knowledge.',
      color: '#9b59b6'
    },
    {
      icon: <FaWallet />,
      title: 'Automated Payouts',
      description: 'Smart contract payouts. Win and get paid instantly.',
      color: '#2ecc71'
    },
    {
      icon: <FaChartLine />,
      title: 'Rollover Challenges',
      description: 'Sequential prediction challenges. Multiply your rewards.',
      color: '#f39c12'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Secure & Transparent',
      description: 'Blockchain-powered platform. Transparent and secure.',
      color: '#e74c3c'
    }
  ];

  // Stats data
  const stats = [
    { number: '50K+', label: 'Active Fans', icon: <FaUsers /> },
    { number: '100K+', label: 'Predictions Made', icon: <FaTicketAlt /> },
    { number: '500+', label: 'Clubs Represented', icon: <FaTrophy /> },
    { number: '$2M+', label: 'Payouts Processed', icon: <FaDollarSign /> }
  ];

  // Clubs represented
  const clubs = [
    { name: 'Arsenal', emoji: '🔴', color: '#ef0107' },
    { name: 'Chelsea', emoji: '🔵', color: '#034694' },
    { name: 'Liverpool', emoji: '🔴', color: '#c8102e' },
    { name: 'Man City', emoji: '🔵', color: '#6cabe4' },
    { name: 'Man United', emoji: '🔴', color: '#da291c' },
    { name: 'Tottenham', emoji: '⚪', color: '#132257' },
    { name: 'Barcelona', emoji: '🔴🔵', color: '#a50044' },
    { name: 'Real Madrid', emoji: '⚪', color: '#00529f' },
    { name: 'Bayern Munich', emoji: '🔴', color: '#dc052d' },
    { name: 'PSG', emoji: '🔴🔵', color: '#004170' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-pulse"></span>
              {isMobile ? '🏆 Fans to Fans' : '🚀 Ultimate Fans to Fans Sports Prediction Marketplace'}
            </div>
            <h1 className="hero-title">
              {isMobile ? (
                <>Predict, Compete & <br /><span className="gradient-text">Win With Your Club</span></>
              ) : (
                <>Predict, Compete & <br /><span className="gradient-text">Win With Your Club</span></>
              )}
            </h1>
            <p className="hero-description">
              {isMobile 
                ? 'Join fans, predict on club games, and get automated payouts when you win.'
                : 'Join the ultimate fans to fans sports prediction marketplace. Predict on your favorite club games, compete with fellow fans, and get automated payouts when you win.'
              }
            </p>
            
            <div className="hero-features">
              <div className="hero-feature">
                <FaCheck className="hf-check" />
                <span>Predict on any match</span>
              </div>
              <div className="hero-feature">
                <FaCheck className="hf-check" />
                <span>Automated payouts</span>
              </div>
              {!isMobile && (
                <div className="hero-feature">
                  <FaCheck className="hf-check" />
                  <span>Win with your club</span>
                </div>
              )}
            </div>

            <div className="hero-actions">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary btn-hero">
                  Dashboard <FaArrowRight />
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-hero">
                    {isMobile ? 'Join Free' : 'Join Free'} <FaRocket />
                  </Link>
                  <Link to="/login" className="btn btn-secondary btn-hero">
                    <FaPlay /> {isMobile ? 'Login' : 'Login'}
                  </Link>
                </>
              )}
            </div>

            <div className="hero-trust">
              <span><FaShieldAlt className="trust-icon" /> Automated Payouts</span>
              <span><FaCheck className="trust-icon" /> 100% Transparent</span>
              {!isMobile && (
                <>
                  <span><FaUsers className="trust-icon" /> 50K+ Fans</span>
                  <span><FaGlobe className="trust-icon" /> Global Platform</span>
                </>
              )}
            </div>
          </div>

          <div className="hero-visual">
            <div className="slider-container">
              <div className="slider-wrapper">
                {slides.map((slide, index) => (
                  <div 
                    key={slide.id} 
                    className={`slide-card ${index === currentSlide ? 'active' : ''}`}
                    style={{ 
                      transform: `translateX(${(index - currentSlide) * 100}%)`,
                      opacity: index === currentSlide ? 1 : 0.3,
                      zIndex: index === currentSlide ? 2 : 1
                    }}
                  >
                    <div className="slide-content">
                     
                      <div className="slide-emoji">{slide.image}</div>
                      <h3 className="slide-title">{slide.title}</h3>
                      <p className="slide-description">{slide.description}</p>
                      <div className="slide-highlight" style={{ background: slide.color }}>
                        <span>{slide.highlight}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="slider-controls">
                <button onClick={prevSlide} className="slider-btn prev">
                  <FaChevronLeft />
                </button>
                <div className="slider-dots">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                    />
                  ))}
                </div>
                <button onClick={nextSlide} className="slider-btn next">
                  <FaChevronRight />
                </button>
              </div>

              <div className="slide-counter">
                {currentSlide + 1} / {slides.length}
              </div>
            </div>

            <div className="clubs-carousel">
              <div className="clubs-scroll">
                {clubs.map((club, index) => (
                  <div key={index} className="club-item">
                    <span className="club-emoji">{club.emoji}</span>
                    <span className="club-name" style={{ color: club.color }}>
                      {club.name}
                    </span>
                  </div>
                ))}
                {clubs.map((club, index) => (
                  <div key={`dup-${index}`} className="club-item">
                    <span className="club-emoji">{club.emoji}</span>
                    <span className="club-name" style={{ color: club.color }}>
                      {club.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-info">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Fans to Fans Section */}
      <section className="fans-section">
        <div className="fans-container">
          <div className="fans-header">
            <span className="section-badge">🔥 Fans to Fans</span>
            <h2>Predict <span className="gradient-text">Alongside Your Club</span></h2>
            <p className="fans-subtitle">
              {isMobile 
                ? 'Join fellow fans, predict on your club\'s matches, and win together.'
                : 'Join fellow fans, predict on your club\'s matches, and win together. When your club wins, you win too!'
              }
            </p>
          </div>
          <div className="fans-grid">
            <div className="fans-card">
              <div className="fans-icon">⚽</div>
              <h3>Predict on Club Games</h3>
              <p>Choose your favorite club and predict match outcomes.</p>
            </div>
            <div className="fans-card highlight">
              <div className="fans-icon">🤝</div>
              <h3>Compete with Fellow Fans</h3>
              <p>Challenge other fans and climb the leaderboard.</p>
            </div>
            <div className="fans-card">
              <div className="fans-icon">💰</div>
              <h3>Automated Payouts</h3>
              <p>Smart contracts handle payouts automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-header">
          <span className="section-badge">Features</span>
          <h2>Everything You Need to <span className="gradient-text">Win</span></h2>
          <p className="features-subtitle">
            {isMobile 
              ? 'Built for fans, by fans. The ultimate sports prediction marketplace.'
              : 'Built for fans, by fans. The ultimate sports prediction marketplace.'
            }
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon-wrapper" style={{ background: `${feature.color}20` }}>
                <span className="feature-icon" style={{ color: feature.color }}>
                  {feature.icon}
                </span>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <div className="feature-line" style={{ background: feature.color }}></div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="how-section">
        <div className="how-container">
          <div className="how-header">
            <span className="section-badge">How It Works</span>
            <h2>Start Winning in <span className="gradient-text">3 Simple Steps</span></h2>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">📝</div>
              <h3>Join the Community</h3>
              <p>Create your free account and choose your favorite clubs.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">🎯</div>
              <h3>Predict & Compete</h3>
              <p>Predict on matches and compete with fellow fans.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">💰</div>
              <h3>Win & Get Paid</h3>
              <p>Get automated payouts instantly when you win.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Punter+ Section */}
      <section className="punter-section">
        <div className="punter-container">
          <div className="punter-content">
            <div className="punter-text">
              
              <h2>Upgrade to <span className="gradient-text">Punter+</span></h2>
              <p>Unlock premium features and dominate the marketplace</p>
              <ul className="punter-benefits">
                <li><FaCheck className="check-icon" /> Create & Monitize private betcode</li>
                <li><FaCheck className="check-icon" /> Create & monitize competitions</li>
                <li><FaCheck className="check-icon" /> Create H2H Game</li>
                <li><FaCheck className="check-icon" /> Create & monitize Rollovers</li>
                {!isMobile && (
                  <>
                    <li><FaCheck className="check-icon" /> Create Subscription plan</li>
                    <li><FaCheck className="check-icon" /> Get Premium badge</li>
                  </>
                )}
              </ul>
              <Link to={isAuthenticated ? "/subscription" : "/register"} className="btn btn-primary">
                {isAuthenticated ? 'Upgrade Now' : 'Join Now'} <FaArrowRight />
              </Link>
            </div>
            <div className="punter-price-card">
              <div className="price-card">
                <div className="price-header">
                  <FaCrown className="price-crown" />
                  <span className="price-plan">Punter+</span>
                </div>
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">2</span>
                  <span className="period">/mo</span>
                </div>
                <div className="price-features">
                  <div className="price-feature"><FaCheck /> All Premium Features</div>
                  <div className="price-feature"><FaCheck /> Unlimited Predictions</div>
                   
                   
                      <div className="price-feature"><FaCheck /> Community Monitization</div>
                      <div className="price-feature"><FaCheck /> Premium Badge</div>
                     
                 
                </div>
                <Link to={isAuthenticated ? "/subscription" : "/register"} className="btn btn-primary btn-sm price-btn">
                  {isAuthenticated ? 'Upgrade Now' : 'Get Started'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <span className="section-badge">Testimonials</span>
            <h2>What Fans Are <span className="gradient-text">Saying</span></h2>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-avatar">JD</div>
              <h4>John Doe</h4>
              <div className="testimonial-club">🔴 Arsenal Fan</div>
              <p>"XPunters is amazing! I predict on every game and win alongside my club."</p>
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-avatar">SM</div>
              <h4>Sarah M.</h4>
              <div className="testimonial-club">🔵 Chelsea Fan</div>
              <p>"The automated payouts are incredible. Win and get paid instantly."</p>
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
            </div>
            {!isMobile && (
              <div className="testimonial-card">
                <div className="testimonial-avatar">MK</div>
                <h4>Mike K.</h4>
                <div className="testimonial-club">🔴 Liverpool Fan</div>
                <p>"Competing with fellow fans makes every match more exciting!"</p>
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <div className="cta-icon">🚀</div>
            <h2>Ready to Win With Your Club?</h2>
            <p>Join the ultimate fans to fans sports prediction marketplace today.</p>
            {!isAuthenticated && (
              <div className="cta-actions">
                <Link to="/register" className="btn btn-primary btn-lg">
                  Create Free Account <FaArrowRight />
                </Link>
                <Link to="/login" className="btn btn-secondary btn-lg">
                  <FaPlay /> Login
                </Link>
              </div>
            )}
            {isAuthenticated && (
              <div className="cta-actions">
                <Link to="/dashboard" className="btn btn-primary btn-lg">
                  Go to Dashboard <FaArrowRight />
                </Link>
              </div>
            )}
            <div className="cta-trust">
              <span>🔒 Automated Payouts</span>
              <span>💳 100% Transparent</span>
              {!isMobile && (
                <>
                  <span>🏦 No Hidden Fees</span>
                  <span>🌍 Global Community</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
        <section className="referral-section">
        <div className="referral-container">
            <div className="referral-header">
            <span className="section-badge">🎁 Refer & Earn</span>
            <h2>Invite Friends, <span className="gradient-text">Earn Passive Income</span></h2>
            <p className="referral-subtitle">
                Share your referral link and earn 20% of admin fees whenever your friends win!
            </p>
            </div>
            <div className="referral-content">
            <div className="referral-card">
                <div className="referral-icon-wrapper">
                <FaUserPlus className="referral-icon" />
                </div>
                <h3>1. Share Your Link</h3>
                <p>Get your unique referral link and share it with friends</p>
                <div className="referral-benefits">
                <div className="referral-benefit">
                    <FaCheck className="rb-check" />
                    <span>20% of admin fees from referrals</span>
                </div>
                <div className="referral-benefit">
                    <FaCheck className="rb-check" />
                    <span>Earn every time your referral wins</span>
                </div>
                <div className="referral-benefit">
                    <FaCheck className="rb-check" />
                    <span>Passive income stream</span>
                </div>
                </div>
            </div>
            <div className="referral-card highlight">
                <div className="referral-icon-wrapper gold">
                <FaPercentage className="referral-icon" />
                </div>
                <h3>2. Friend Joins & Wins</h3>
                <p>Your friend joins using your link and starts winning predictions</p>
                <div className="referral-steps">
                <div className="step-badge">1</div>
                <span>Friend registers with your link</span>
                <div className="step-badge">2</div>
                <span>They make predictions & win</span>
                <div className="step-badge">3</div>
                <span>You earn 20% of admin fees</span>
                </div>
            </div>
            <div className="referral-card">
                <div className="referral-icon-wrapper green">
                <FaDollarSign className="referral-icon" />
                </div>
                <h3>3. Get Paid Automatically</h3>
                <p>No manual claims. Earnings are automatically credited to your wallet</p>
                <div className="referral-rewards">
                <div className="reward-item">
                    <span className="reward-amount">20%</span>
                    <span className="reward-label">Of admin fees</span>
                </div>
                <div className="reward-divider"></div>
                <div className="reward-item">
                    <span className="reward-amount">Auto</span>
                    <span className="reward-label">Instant payouts</span>
                </div>
                <div className="reward-divider"></div>
                <div className="reward-item">
                    <span className="reward-amount">Unlimited</span>
                    <span className="reward-label">Referral earnings</span>
                </div>
                </div>
            </div>
            </div>
            <div className="referral-cta">
            {isAuthenticated ? (
                <Link to="/referral" className="btn btn-primary btn-lg">
                View Referral Dashboard <FaArrowRight />
                </Link>
            ) : (
                <Link to="/register" className="btn btn-primary btn-lg">
                Start Earning <FaRocket />
                </Link>
            )}
            </div>
        </div>
        </section>
    </div>
  );
};

export default Home;